import React, {useEffect, useRef} from "react";
import {useTheme} from "../context/ThemeContext";

type Point = {
  x: number;
  y: number;
  value: number;
};

// Mirrors tokens.css's --background-secondary / --gray-100 / --text-muted for
// the two themes — canvas fillStyle can't read CSS custom properties, so the
// palette the animation draws with has to be duplicated here rather than
// referencing var(...) directly.
const PALETTES = {
  light: {
    cardBg: "#FFFFFF",
    grid: "rgba(203, 213, 225, 0.2)",
    todayLine: "rgba(148, 163, 184, 0.6)",
    axisLabel: "#64748B",
    legendLabel: "#475569",
    outcomeArea: "rgba(241, 245, 249, 0.5)",
    badgeBg: "rgba(255, 255, 255, 0.92)",
    badgeBorder: "rgba(203, 213, 225, 0.5)",
    pointFill: "#FFFFFF",
    shadow: "0 10px 30px rgba(0, 0, 0, 0.1)",
  },
  dark: {
    cardBg: "#131C2E",
    grid: "rgba(148, 163, 184, 0.15)",
    todayLine: "rgba(148, 163, 184, 0.45)",
    axisLabel: "#94A3B8",
    legendLabel: "#CBD5E1",
    outcomeArea: "rgba(255, 255, 255, 0.04)",
    badgeBg: "rgba(19, 28, 46, 0.92)",
    badgeBorder: "rgba(100, 116, 139, 0.5)",
    pointFill: "#131C2E",
    shadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
  },
};

const CanvasHeroChart = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {theme} = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const palette = PALETTES[theme];
    let animationFrameId: number;
    let time: number = 0;

    // For high pixel density displays
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    // Chart data
    const mainData = [180, 175, 160, 165, 150, 130, 120, 140, 110, 100, 120];
    const outcomes = [
      {data: [120, 128, 140, 155, 170], color: "#059669", label: "Match 1"},  // strong rally
      {data: [120, 124, 132, 140, 148], color: "#10B981", label: "Match 2"},  // steady climb
      {data: [120, 126, 130, 145, 152], color: "#0891B2", label: "Match 3"},  // up with dip
      {data: [120, 118, 125, 130, 128], color: "#F59E0B", label: "Match 4"},  // mostly up, choppy
      {data: [120, 115, 108, 104, 95],  color: "#DC2626", label: "Match 5"},  // outlier down
    ];

    // Shared Y-scale across all outcome data
    const allOutcomeValues = outcomes.flatMap(o => o.data);
    const allValues = [...mainData, ...allOutcomeValues];
    const globalMin = Math.min(...allValues) * 0.9;
    const globalMax = Math.max(...allValues) * 1.1;

    // Calculate dimensions
    const width = rect.width;
    const height = rect.height;
    const padding = {top: 40, right: 40, bottom: 60, left: 40};
    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Animation params
    const lineAnimationSpeed = 0.2;
    const dashLength = 5;
    const outcomeStaggerDelay = 0.35; // time units between each outcome line start

    // Function to map data points to coordinates with shared scale
    const mapToCoordinates = (
      data: number[],
      startX: number,
      endX: number,
    ): Point[] => {
      const step = (endX - startX) / (data.length - 1);
      const yScale = chartHeight / (globalMax - globalMin);

      return data.map((value, index) => ({
        x: startX + index * step,
        y: padding.top + chartHeight - (value - globalMin) * yScale,
        value,
      }));
    };

    // Function to draw the background grid
    const drawGrid = (): void => {
      ctx.save();
      ctx.strokeStyle = palette.grid;
      ctx.lineWidth = 1;

      for (let x = padding.left; x <= width - padding.right; x += 30) {
        ctx.beginPath();
        ctx.moveTo(x, padding.top);
        ctx.lineTo(x, height - padding.bottom);
        ctx.stroke();
      }

      for (let y = padding.top; y <= height - padding.bottom; y += 30) {
        ctx.beginPath();
        ctx.moveTo(padding.left, y);
        ctx.lineTo(width - padding.right, y);
        ctx.stroke();
      }
      ctx.restore();
    };

    // Draw "Today" vertical dashed line
    const drawTodayLine = (x: number): void => {
      ctx.save();
      ctx.strokeStyle = palette.todayLine;
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, padding.top);
      ctx.lineTo(x, height - padding.bottom);
      ctx.stroke();

      // "Today" label — right-aligned so it sits over the query side, ending just
      // left of the divider line (clear of the outcome-area label to its right).
      ctx.setLineDash([]);
      ctx.fillStyle = palette.axisLabel;
      ctx.font = "11px Inter, sans-serif";
      ctx.textAlign = "right";
      ctx.fillText("Today", x - 6, padding.top - 8);
      ctx.restore();
    };

    // Draw outcome area background
    const drawOutcomeArea = (startX: number): void => {
      const areaWidth = width - padding.right - startX;

      ctx.save();
      ctx.fillStyle = palette.outcomeArea;
      ctx.fillRect(startX, padding.top, areaWidth, chartHeight);

      // Label left-aligned starting just right of the divider line — keeps it on the
      // outcome side, never overlapping the right-aligned "Today" label on the left.
      ctx.fillStyle = palette.axisLabel;
      ctx.font = "11px Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("Historical Outcomes", startX + 6, padding.top - 8);
      ctx.restore();
    };

    // Draw the line connecting data points
    const drawLine = (
      points: Point[],
      color: string,
      lineWidth: number,
      dashPattern: number[] = [],
      animationProgress: number = 1,
    ): void => {
      if (points.length < 2) return;

      const visiblePoints = Math.ceil(points.length * animationProgress);
      if (visiblePoints < 2) return;

      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      if (dashPattern.length) {
        ctx.setLineDash(dashPattern);
      }

      ctx.beginPath();
      ctx.moveTo(points[0].x, points[0].y);

      for (let i = 1; i < visiblePoints; i++) {
        if (i === visiblePoints - 1 && animationProgress < 1) {
          const progress = (animationProgress * points.length) % 1;
          const prevPoint = points[i - 1];
          const currPoint = points[i];
          const x = prevPoint.x + (currPoint.x - prevPoint.x) * progress;
          const y = prevPoint.y + (currPoint.y - prevPoint.y) * progress;
          ctx.lineTo(x, y);
        } else {
          ctx.lineTo(points[i].x, points[i].y);
        }
      }

      ctx.stroke();
      ctx.restore();
    };

    // Draw data points — a circle appears only once the line has reached it
    const drawDataPoints = (
      points: Point[],
      color: string,
      radius: number,
      animationProgress: number,
    ): void => {
      ctx.save();
      ctx.fillStyle = palette.pointFill;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;

      // The line has fully reached point i when progress >= (i+1)/points.length
      // Use floor so the dot appears exactly when the line arrives, not before
      const reachedIndex = Math.min(
        points.length - 1,
        Math.floor(animationProgress * (points.length - 1)),
      );

      for (let i = 0; i <= reachedIndex; i++) {
        const point = points[i];
        ctx.beginPath();
        ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }

      ctx.restore();
    };

    // Draw bullish summary badge — fades in after all outcomes finish
    const drawBullishBadge = (opacity: number, startX: number): void => {
      if (opacity <= 0) return;
      ctx.save();
      ctx.globalAlpha = opacity;

      const bullishCount = 4;
      const totalCount = outcomes.length;
      const bullishFraction = bullishCount / totalCount;

      // Position: centered in the outcome area, vertically centered
      const areaWidth = width - padding.right - startX;
      const centerX = startX + areaWidth / 2;
      const centerY = padding.top + chartHeight * 0.18;

      // Background pill
      const pillWidth = 110;
      const pillHeight = 36;
      const pillX = centerX - pillWidth / 2;
      const pillY = centerY - pillHeight / 2;
      const pillRadius = 8;

      ctx.fillStyle = palette.badgeBg;
      ctx.beginPath();
      ctx.moveTo(pillX + pillRadius, pillY);
      ctx.lineTo(pillX + pillWidth - pillRadius, pillY);
      ctx.quadraticCurveTo(pillX + pillWidth, pillY, pillX + pillWidth, pillY + pillRadius);
      ctx.lineTo(pillX + pillWidth, pillY + pillHeight - pillRadius);
      ctx.quadraticCurveTo(pillX + pillWidth, pillY + pillHeight, pillX + pillWidth - pillRadius, pillY + pillHeight);
      ctx.lineTo(pillX + pillRadius, pillY + pillHeight);
      ctx.quadraticCurveTo(pillX, pillY + pillHeight, pillX, pillY + pillHeight - pillRadius);
      ctx.lineTo(pillX, pillY + pillRadius);
      ctx.quadraticCurveTo(pillX, pillY, pillX + pillRadius, pillY);
      ctx.closePath();
      ctx.fill();

      // Subtle border
      ctx.strokeStyle = palette.badgeBorder;
      ctx.lineWidth = 1;
      ctx.stroke();

      // "4/5 Bullish" text
      ctx.fillStyle = "#059669";
      ctx.font = "bold 14px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(`${bullishCount}/${totalCount} Bullish`, centerX, centerY + 1);

      // Small direction bar underneath
      const barWidth = 60;
      const barHeight = 3;
      const barX = centerX - barWidth / 2;
      const barY = centerY + 10;

      // Red (bearish) background
      ctx.fillStyle = "#DC2626";
      ctx.beginPath();
      ctx.roundRect(barX, barY, barWidth, barHeight, 1.5);
      ctx.fill();

      // Green (bullish) fill
      ctx.fillStyle = "#059669";
      ctx.beginPath();
      ctx.roundRect(barX, barY, barWidth * bullishFraction, barHeight, 1.5);
      ctx.fill();

      ctx.restore();
    };

    // Draw the chart legend — compact dot format. The 5 outcome lines collapse into a
    // single "Historical matches" cluster (a row of their colored dots + one label) so
    // the legend stays narrow enough to never clip; centered across the FULL canvas
    // width with a margin clamp (the old version centered 6 separate items across the
    // inner chartWidth and overflowed both edges, clipping "Your Stock" and "Match 5").
    const drawLegend = (showOutcomes: boolean): void => {
      ctx.save();

      const legendY = height - padding.bottom + 30;
      const dotRadius = 4;
      const gap = 6; // space between dot(s) and label
      const dotGap = 3; // space between clustered dots
      const itemGap = 16; // space between legend groups
      const margin = 8; // min distance from the canvas edge

      ctx.font = "11px Inter, sans-serif";
      ctx.textBaseline = "middle";

      // Each segment = a row of one-or-more colored dots followed by a single label.
      const segments: {colors: string[]; label: string}[] = [
        {colors: ["#3B82F6"], label: "Your Stock"},
        ...(showOutcomes
          ? [{colors: outcomes.map(o => o.color), label: "Historical matches"}]
          : []),
      ];

      const dotsWidth = (n: number) => n * dotRadius * 2 + (n - 1) * dotGap;
      const segWidths = segments.map(
        s => dotsWidth(s.colors.length) + gap + ctx.measureText(s.label).width,
      );
      const totalWidth =
        segWidths.reduce((a, b) => a + b, 0) + itemGap * (segments.length - 1);

      let x = Math.max(margin, (width - totalWidth) / 2);

      ctx.textAlign = "left";
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i];

        let dx = x;
        for (const color of seg.colors) {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.arc(dx + dotRadius, legendY, dotRadius, 0, Math.PI * 2);
          ctx.fill();
          dx += dotRadius * 2 + dotGap;
        }

        ctx.fillStyle = palette.legendLabel;
        ctx.fillText(seg.label, dx - dotGap + gap, legendY);

        x += segWidths[i] + itemGap;
      }

      ctx.restore();
    };

    // Main animation function
    const animate = (): void => {
      time += 0.02;
      ctx.clearRect(0, 0, width, height);

      drawGrid();

      // Map main data to coordinates (60% of chart width)
      const mainLineEndX = padding.left + chartWidth * 0.6;
      const mainLinePoints = mapToCoordinates(mainData, padding.left, mainLineEndX);
      const matchPoint = mainLinePoints[mainLinePoints.length - 1];

      // Calculate main line animation progress
      const mainLineProgress = Math.min(1, time * lineAnimationSpeed);
      const isMainLineComplete = mainLineProgress >= 0.99;

      // Draw the main line
      drawLine(mainLinePoints, "#3B82F6", 3, [], mainLineProgress);
      drawDataPoints(mainLinePoints, "#3B82F6", 5, mainLineProgress);

      // Phase 2 & 3: After main line completes
      if (isMainLineComplete) {
        // Pause duration in time units (~0.3s at 0.02 per frame = 15 frames = 0.3 time units)
        const pauseDuration = 0.3;
        const mainCompleteTime = 1 / lineAnimationSpeed; // time when main line completed
        const timeSinceComplete = time - mainCompleteTime;

        // Draw "Today" line immediately after main completes
        if (timeSinceComplete > 0) {
          drawTodayLine(matchPoint.x);
        }

        // Start outcomes after pause
        const outcomeStartTime = pauseDuration;
        const anyOutcomeStarted = timeSinceComplete > outcomeStartTime;

        if (anyOutcomeStarted) {
          // Draw outcome area background
          drawOutcomeArea(matchPoint.x);

          // Map and draw each outcome line with stagger
          const outcomeEndX = width - padding.right;
          let allOutcomesComplete = true;
          for (let i = 0; i < outcomes.length; i++) {
            const outcome = outcomes[i];
            const staggerTime = outcomeStartTime + i * outcomeStaggerDelay;
            const timeSinceOutcomeStart = timeSinceComplete - staggerTime;

            if (timeSinceOutcomeStart > 0) {
              const outcomeProgress = Math.min(1, timeSinceOutcomeStart * 3);
              if (outcomeProgress < 1) allOutcomesComplete = false;

              const outcomePoints = mapToCoordinates(
                outcome.data,
                matchPoint.x,
                outcomeEndX,
              );
              // Ensure first point matches exactly
              outcomePoints[0] = {...matchPoint};

              drawLine(
                outcomePoints,
                outcome.color,
                2,
                [dashLength, dashLength],
                outcomeProgress,
              );
            } else {
              allOutcomesComplete = false;
            }
          }

          // Fade in bullish badge after all outcomes finish
          if (allOutcomesComplete) {
            const lastOutcomeEndTime =
              outcomeStartTime +
              (outcomes.length - 1) * outcomeStaggerDelay +
              1 / 3; // 1/3 is the duration at speed *3
            const badgeFadeProgress = Math.min(
              1,
              (timeSinceComplete - lastOutcomeEndTime) * 2.5,
            );
            drawBullishBadge(badgeFadeProgress, matchPoint.x);
          }
        }

        drawLegend(anyOutcomeStarted);
      } else {
        drawLegend(false);
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [theme]);

  const palette = PALETTES[theme];

  return (
    <div className="hero-chart-canvas" style={{width: "100%", height: "100%"}}>
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: "12px",
          backgroundColor: palette.cardBg,
          boxShadow: palette.shadow,
        }}
      />
    </div>
  );
};

export default CanvasHeroChart;
